"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="100094" className="flex gap-8">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-4 w-20 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => {
        const isTreatments = String(item.id) === "858";
        if (isTreatments) {
          return <NavigationMenuItem key={item.id} data-index={i}>
                <NavigationMenuTrigger className="group relative inline-flex items-center h-auto bg-transparent p-0 text-[12px] font-medium uppercase tracking-[0.1em] font-sans text-foreground transition-colors hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent shadow-none border-none cursor-pointer">
                  {item.label}
                  <span className="absolute bottom-0 left-1/2 h-[1px] w-0 -translate-x-1/2 bg-primary transition-all duration-500 group-hover:w-full data-[state=open]:w-full" />
                </NavigationMenuTrigger>
                <NavigationMenuContent className="bg-background/95 backdrop-blur-md border border-border/50 p-4 min-w-[220px] shadow-xl">
                  <ul className="flex flex-col gap-1">
                    {item.children?.map(child => <li key={child.id}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block px-4 py-2 text-[11px] uppercase tracking-wider text-muted-foreground hover:text-primary hover:bg-accent/30 transition-all rounded-sm">
                            {child.label}
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>;
        }
        return <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="group relative inline-block py-2 text-[12px] font-medium uppercase tracking-[0.1em] font-sans text-foreground transition-colors hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent">
                  {item.label}
                  <span className="absolute bottom-0 left-1/2 h-[1px] w-0 -translate-x-1/2 bg-primary transition-all duration-500 group-hover:w-full" />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  if (loading) {
    return <div className="flex flex-col gap-6">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-6 w-32 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6">
      {menuItems.map((item, i) => {
      const isExpanded = expandedId === String(item.id);
      const isTreatments = String(item.id) === "858";
      const hasChildren = isTreatments && item.children && item.children.length > 0;
      return <div key={item.id} data-index={i} className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <Link to={item.href || "#"} onClick={onItemClick} className="text-sm font-medium uppercase tracking-[0.1em] text-foreground hover:text-primary transition-colors font-sans">
                {item.label}
              </Link>
              {hasChildren && <Button variant="ghost" size="icon" onClick={() => setExpandedId(isExpanded ? null : String(item.id))} className="size-8 text-foreground/50 hover:text-primary hover:bg-transparent">
                  <ChevronDown className={`size-4 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} />
                </Button>}
            </div>
            {hasChildren && isExpanded && <div className="pl-4 flex flex-col gap-4 border-l border-primary/20 ml-1 animate-in fade-in slide-in-from-top-1 duration-300">
                {item.children?.map(child => <Link key={child.id} to={child.href || "#"} onClick={onItemClick} className="text-[13px] font-medium uppercase tracking-[0.1em] text-muted-foreground hover:text-primary transition-colors font-sans">
                    {child.label}
                  </Link>)}
              </div>}
          </div>;
    })}
    </nav>;
}
export default function UstMenu() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="üst-bilgi" className={`sticky top-0 z-50 w-full transition-all duration-500 bg-background text-foreground ${isScrolled ? "bg-background/70 backdrop-blur-lg border-b border-border shadow-sm" : "bg-background border-b border-transparent"}`}>
      <div className="container mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        
        {}
        <div className="md:hidden flex-shrink-0">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle Menu" className="text-foreground hover:bg-secondary/50">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-start">
          <MenuProvider menu_id="30">
            <DesktopNav />
          </MenuProvider>
        </div>

        {}
        <div className="flex-shrink-0 flex justify-end">
          <Link to="#" className="block">
            <img src="https://dtaysegul.10web.cloud/wp-content/uploads/2026/04/aysss-01-1.png" alt="Diş Hekimi Ayşegül İnneci Uzun" className="object-contain brightness-90 contrast-125 !w-[260px] !h-[100px]" />
          </Link>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border p-8 shadow-xl md:hidden animate-in slide-in-from-top-2 duration-500">
          <MenuProvider menu_id="30">
            <MobileNav onItemClick={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}